import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { type Worker } from "@src/appV2/Worker/api/types";
import { useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { RootPaths } from "../App/paths";
import { useGetWorkplace } from "../Facilities/api/useGetWorkplace";
import { IconButton } from "../redesign/components/IconButton";
import { AllRolesFilterProvider } from "../redesign/ShiftDiscovery/Blocks/hooks/useAllRolesFilter";
import { ShiftBlockFilter } from "./components/ShiftBlockFilter";
import { ShiftBlocks } from "./components/ShiftBlocks";

interface ShiftBlockPageProps {
  agent: Worker;
  facilityId?: string;
  orientationBlockQualification?: string;
}

interface ShiftBlockPageHeaderProps {
  facilityName?: string;
  agent: Worker;
  orientationBlockQualification?: string;
  onBack: () => void;
}

function ShiftBlockPageHeader(props: ShiftBlockPageHeaderProps) {
  const { facilityName, agent, orientationBlockQualification, onBack } = props;
  const isOrientationBlock = isDefined(orientationBlockQualification);

  if (!isOrientationBlock && agent.licensesData.length <= 1) {
    return null;
  }

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.5}>
      <IconButton iconType="chevron-left" variant="outlined" onClick={onBack} />

      <Box>
        {isOrientationBlock ? (
          <Stack alignItems="center">
            {isDefined(facilityName) && <Text bold>{facilityName}</Text>}
            <Text>{orientationBlockQualification} Orientation Blocks</Text>
          </Stack>
        ) : (
          <Stack direction="row" alignItems="center" gap={1}>
            <Text>Block for</Text>
            <ShiftBlockFilter worker={agent} />
          </Stack>
        )}
      </Box>

      {/* Placeholder so the button and text can be positioned left and center
      respectively using space-between */}
      <Box sx={{ visibility: "hidden" }}>
        <IconButton iconType="chevron-left" variant="outlined" />
      </Box>
    </Stack>
  );
}

export function ShiftBlockPage(props: ShiftBlockPageProps) {
  const { agent, facilityId, orientationBlockQualification } = props;
  const history = useHistory();
  const location = useLocation();

  const { data: workplace } = useGetWorkplace(
    { workplaceId: facilityId },
    { enabled: isDefined(facilityId) }
  );

  const facilityName = workplace?.data?.attributes?.name;

  // Read "allRoles" query param to initialize filter state (e.g., from callout navigation)
  const initialAllRolesSelected = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("allRoles") === "true";
  }, [location.search]);

  const handleGoBack = useCallback(() => {
    if (isDefined(location.key)) {
      history.goBack();
    } else {
      history.push(RootPaths.APP_V2_HOME);
    }
  }, [history, location.key]);

  return (
    <AllRolesFilterProvider initialValue={initialAllRolesSelected}>
      <Stack sx={{ overflowY: "auto", p: 2 }} flex={1} spacing={2}>
        <ShiftBlockPageHeader
          facilityName={facilityName}
          agent={agent}
          orientationBlockQualification={orientationBlockQualification}
          onBack={handleGoBack}
        />
        <ShiftBlocks
          worker={agent}
          facilityId={facilityId}
          orientationBlockQualification={orientationBlockQualification}
        />
      </Stack>
    </AllRolesFilterProvider>
  );
}
